import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  getDocs,
  orderBy,
  limit,
  Timestamp,
  addDoc,
  serverTimestamp,
  startAfter,
  DocumentSnapshot,
  writeBatch,
  increment
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './config';
import { cache } from '../utils/cacheManager';

// Collection names
export const COLLECTIONS = {
  USERS: 'users',
  PROGRAMS: 'programs',
  PROGRAM_REGISTRATIONS: 'programRegistrations',
  LIBRARY_MEMBERS: 'libraryMembers',
  LIBRARY_ATTENDANCE: 'libraryAttendance',
  NEWS: 'news',
  SITE_VISITS: 'siteVisits',
  EVENTS: 'events',
  DONATIONS: 'donations',
  FEEDBACK: 'feedback',
  SETTINGS: 'settings'
};

const PUBLIC_LIST_CACHE_TTL = 60 * 60 * 1000; // 1 hour
const PUBLIC_DETAIL_CACHE_TTL = 2 * 60 * 60 * 1000; // 2 hours
const PUBLIC_STATIC_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const PUBLIC_NEWS_CACHE_VERSION = 'v2';

const invalidateNewsCaches = () => {
  cache.invalidatePattern(/news/);
  cache.invalidatePattern(/news_trends_/);
  cache.invalidatePattern(/home_carousel/);
  cache.invalidatePattern(/home_cards/);
  cache.invalidatePattern(/^home\.news\./);
  cache.invalidate('home_carousel_news');
  cache.invalidate('home_cards_news');
  cache.invalidate('news_dashboard_stats');
  cache.invalidate('home_featured_programs');
};

const looksLikeId = (value: string) => /^[A-Za-z0-9_-]{20,}$/.test(value);

const formatStoredAuthorName = (authorValue: any, fallback = 'Foundation Team'): string => {
  if (!authorValue) return fallback;

  if (typeof authorValue === 'object') {
    const objectName = authorValue.firstName || authorValue.displayName || authorValue.name;
    if (typeof objectName === 'string' && objectName.trim()) {
      return objectName.trim().split(' ')[0];
    }
    return fallback;
  }

  const authorText = String(authorValue).trim();
  if (!authorText || looksLikeId(authorText)) return fallback;

  return authorText.includes(' ') ? authorText.split(' ')[0] : authorText;
};

const formatFirestoreDate = (primary: any, fallback: any): string => {
  try {
    if (primary?.toDate) return primary.toDate().toISOString();
    if (fallback?.toDate) return fallback.toDate().toISOString();
    if (typeof primary === 'string' || typeof primary === 'number') return new Date(primary).toISOString();
    if (typeof fallback === 'string' || typeof fallback === 'number') return new Date(fallback).toISOString();
  } catch (error) {
    console.error('Error parsing date:', error);
  }

  return new Date().toISOString();
};

const mapNewsDoc = (
  newsDoc: DocumentSnapshot,
  options: { excerptLength?: number; defaultCategory?: string; defaultImage?: string } = {}
) => {
  const data = newsDoc.data() as any;
  const excerptLength = options.excerptLength ?? 150;
  const date = formatFirestoreDate(data.publishedAt, data.createdAt);

  return {
    id: newsDoc.id,
    title: data.title || '',
    content: data.content || '',
    excerpt: data.excerpt || (data.content ? data.content.substring(0, excerptLength) + '...' : 'Read more...'),
    imageUrl: data.imageUrl || options.defaultImage || '',
    newsCardImageUrl: data.newsCardImageUrl || '',
    category: data.category || options.defaultCategory || 'General',
    author: formatStoredAuthorName(data.authorName || data.authorDisplayName || data.author),
    date,
    createdAt: formatFirestoreDate(data.createdAt, data.publishedAt),
    updatedAt: formatFirestoreDate(data.updatedAt, data.createdAt || data.publishedAt),
    isPublished: data.isPublished || false,
    showInCarousel: data.showInCarousel || false,
    showInCards: data.showInCards || false,
    showInGeneral: data.showInGeneral !== false,
    newsCardImagePositionX: typeof data.newsCardImagePositionX === 'number' ? data.newsCardImagePositionX : 50,
    newsCardImagePositionY: typeof data.newsCardImagePositionY === 'number' ? data.newsCardImagePositionY : 50,
    tags: data.tags || [],
    views: data.views || 0,
    youtubeUrl: data.youtubeUrl || '',
    isUpcoming: data.isUpcoming || false,
    otherImages: data.otherImages || [],
  };
};



// In createNews function, update the cache invalidation:
export const createNews = async (newsData: any, authorId: string) => {
  try {
    // Try to resolve author display name to store alongside uid
    let authorName = '';
    try {
      const profile = await getUserProfile(authorId);
      if (profile.success && profile.data) {
        authorName = (profile.data.firstName && profile.data.firstName.trim())
          ? profile.data.firstName.trim()
          : (profile.data.displayName || profile.data.name || '');
      }
    } catch (e) {}

    const docRef = await addDoc(collection(db, COLLECTIONS.NEWS), {
      ...newsData,
      author: authorId,
      authorName: authorName,
      views: 0,
      showInCarousel: newsData.showInCarousel || false,
      showInCards: newsData.showInCards || false,
      showInGeneral: newsData.showInGeneral !== false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      publishedAt: newsData.isPublished ? serverTimestamp() : null,
    });
    
    // Clear ALL news-related caches
    invalidateNewsCaches();
    
    return { success: true, id: docRef.id };
  } catch (error: any) {
    console.error('Error creating news:', error);
    return { success: false, error: error.message };
  }
};


// In updateNews function, update the cache invalidation:
export const updateNews = async (id: string, newsData: any) => {
  try {
    const newsDoc = doc(db, COLLECTIONS.NEWS, id);
    // If authorId provided, resolve and update authorName too
    const updatePayload: any = {
      ...newsData,
      updatedAt: serverTimestamp(),
      publishedAt: newsData.isPublished ? serverTimestamp() : null,
    };

    if (newsData.author && typeof newsData.author === 'string') {
      try {
        const profile = await getUserProfile(newsData.author);
        if (profile.success && profile.data) {
          updatePayload.authorName = (profile.data.firstName && profile.data.firstName.trim())
            ? profile.data.firstName.trim()
            : (profile.data.displayName || profile.data.name || '');
        }
      } catch (e) {}
    }

    await updateDoc(newsDoc, updatePayload);
    
    // Clear ALL news-related caches
    invalidateNewsCaches();
    
    return { success: true };
  } catch (error: any) {
    console.error('Error updating news:', error);
    return { success: false, error: error.message };
  }
};

export const getNewsForCarousel = async (limitCount: number = 5) => {
  const cacheKey = `home_carousel_news_${limitCount}`;
  return await cache.get(cacheKey, async () => {
    try {
      console.log('🔄 Fetching carousel news from Firebase...');

      const q = query(
        collection(db, COLLECTIONS.NEWS),
        where('isPublished', '==', true),
        where('showInCarousel', '==', true),
        orderBy('publishedAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      console.log(`✅ Found ${querySnapshot.size} carousel news items`);

      const news = querySnapshot.docs.map((doc) =>
        mapNewsDoc(doc, {
          defaultCategory: 'News',
          defaultImage: 'https://images.unsplash.com/photo-1559028012-481c04fa702d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80'
        })
      );
      console.log('📦 Carousel news data:', news);
      return { success: true, data: news };
    } catch (error: any) {
      console.error('❌ Error in getNewsForCarousel:', error);
      return { success: false, error: error.message, data: [] };
    }
  }, PUBLIC_LIST_CACHE_TTL);
};

export const getNewsForCards = async (limitCount: number = 6) => {
  const cacheKey = `home_cards_news_${limitCount}`;
  return await cache.get(cacheKey, async () => {
    try {
      console.log('🔄 Fetching cards news from Firebase...');

      const q = query(
        collection(db, COLLECTIONS.NEWS),
        where('isPublished', '==', true),
        where('showInCards', '==', true),
        orderBy('publishedAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      console.log(`✅ Found ${querySnapshot.size} card news items`);

      const news = querySnapshot.docs.map((doc) =>
        mapNewsDoc(doc, {
          excerptLength: 120,
          defaultCategory: 'Update',
          defaultImage: 'https://images.unsplash.com/photo-1559028012-481c04fa702d?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
        })
      );

      console.log('📦 Cards news data:', news);
      return { success: true, data: news };
    } catch (error: any) {
      console.error('❌ Error in getNewsForCards:', error);
      return { success: false, error: error.message, data: [] };
    }
  }, PUBLIC_LIST_CACHE_TTL);
};

export const getGeneralNews = async (options: {
  limitCount?: number;
  category?: string;
  lastDoc?: DocumentSnapshot;
  page?: number;
} = {}) => {
  try {
    const {
      limitCount = 10,
      category,
      lastDoc,
      page = 1
    } = options;

    const cacheKey = `${PUBLIC_NEWS_CACHE_VERSION}_general_news_page_${page}_${limitCount}_${category || 'all'}`;

    // Try cache first
    const cached = cache.getCacheState(cacheKey);
    if (cached.hasCache && cached.isFresh) {
      const cachedData = await cache.get(cacheKey, async () => ({ success: true, data: [] }));
      return { 
        ...cachedData, 
        fromCache: true 
      };
    }

    let constraints: any[] = [
      where('isPublished', '==', true),
      where('showInGeneral', '==', true),
      orderBy('publishedAt', 'desc')
    ];
    
    if (category) {
      constraints.push(where('category', '==', category));
    }
    
    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }
    
    constraints.push(limit(limitCount));
    
    const q = query(collection(db, COLLECTIONS.NEWS), ...constraints);
    const querySnapshot = await getDocs(q);
    
    const news = querySnapshot.docs.map((doc) => mapNewsDoc(doc));
    
    const result = { 
      success: true, 
      data: news,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1],
      fromCache: false
    };
    
    await cache.get(cacheKey, async () => result, PUBLIC_LIST_CACHE_TTL);
    
    return result;
  } catch (error: any) {
    return { success: false, error: error.message, data: [] };
  }
};

// Add these functions to firestoreService.ts

// Get news statistics for dashboard
export const getNewsDashboardStats = async (): Promise<{
  success: boolean;
  data?: {
    totalNews: number;
    publishedNews: number;
    draftNews: number;
    scheduledNews: number;
    totalViews: number;
    averageViews: number;
    topCategories: Array<{ category: string; count: number }>;
    trendingNews: Array<{ id: string; title: string; views: number; publishedAt: string }>;
    monthlyStats: Array<{ month: string; published: number; views: number }>;
    carouselCount: number;
    cardsCount: number;
    generalCount: number;
  };
  error?: string;
}> => {
  // Cache heavy dashboard computation for a short period to reduce reads
  const cacheKey = 'news_dashboard_stats';
  return await cache.get(cacheKey, async () => {
    try {
      // Get all news
      const newsResult = await getNews({ onlyPublished: false });
      if (!newsResult.success) {
        throw new Error('Failed to fetch news');
      }
      
      const allNews = newsResult.data;
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      // Calculate statistics
      const stats = {
        totalNews: allNews.length,
        publishedNews: allNews.filter(n => n.isPublished).length,
        draftNews: allNews.filter(n => !n.isPublished).length,
        scheduledNews: allNews.filter(n => {
          const publishedAt = n.date ? new Date(n.date) : null;
          return n.isPublished && publishedAt && publishedAt > now;
        }).length,
        totalViews: allNews.reduce((sum, news) => sum + (news.views || 0), 0),
        averageViews: 0,
        topCategories: [] as Array<{ category: string; count: number }> ,
        trendingNews: [] as Array<{ id: string; title: string; views: number; publishedAt: string }>,
        monthlyStats: [] as Array<{ month: string; published: number; views: number }>,
        carouselCount: allNews.filter(n => n.showInCarousel).length,
        cardsCount: allNews.filter(n => n.showInCards).length,
        generalCount: allNews.filter(n => n.showInGeneral).length
      };
      
      // Calculate average views
      if (stats.publishedNews > 0) {
        stats.averageViews = Math.round(stats.totalViews / stats.publishedNews);
      }
      
      // Get top categories
      const categoryMap = new Map<string, number>();
      allNews.forEach(news => {
        const category = news.category || 'Uncategorized';
        categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
      });
      stats.topCategories = Array.from(categoryMap.entries())
        .map(([category, count]) => ({ category, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);
      
      // Get trending news (top 5 by views)
      stats.trendingNews = allNews
        .filter(n => n.isPublished)
        .sort((a, b) => (b.views || 0) - (a.views || 0))
        .slice(0, 5)
        .map(news => ({
          id: news.id,
          title: news.title,
          views: news.views || 0,
          publishedAt: news.date || ''
        }));
      
      // Generate monthly stats (last 6 months)
      const monthlyData: Record<string, { published: number; views: number }> = {};
      for (let i = 5; i >= 0; i--) {
        const date = new Date(currentYear, currentMonth - i, 1);
        const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        monthlyData[monthKey] = { published: 0, views: 0 };
      }
      
      // Aggregate monthly data
      allNews.forEach(news => {
        if (news.date) {
          const newsDate = new Date(news.date);
          const monthKey = newsDate.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
          if (monthlyData[monthKey]) {
            if (news.isPublished) {
              monthlyData[monthKey].published++;
            }
            monthlyData[monthKey].views += news.views || 0;
          }
        }
      });
      
      stats.monthlyStats = Object.entries(monthlyData).map(([month, data]) => ({
        month,
        ...data
      }));
      
      return { success: true, data: stats };
    } catch (error: any) {
      console.error('Error getting news dashboard stats:', error);
      return { success: false, error: error.message };
    }
  }, 5 * 60 * 1000);
};

// Get news performance trends
export const getNewsTrends = async (period: 'weekly' | 'monthly' | 'yearly' = 'monthly'): Promise<{
  success: boolean;
  data?: Array<{
    period: string;
    published: number;
    views: number;
    engagementRate: number;
    topPerforming?: { title: string; views: number };
  }>;
  error?: string;
}> => {
  // Cache trends per period to avoid repeated heavy aggregations
  const key = `news_trends_${period}`;
  const ttlMap: Record<string, number> = {
    weekly: 10 * 60 * 1000,
    monthly: 30 * 60 * 1000,
    yearly: 60 * 60 * 1000
  };

  return await cache.get(key, async () => {
    try {
      const newsResult = await getNews({ onlyPublished: true });
      if (!newsResult.success) {
        throw new Error('Failed to fetch news');
      }

      const allNews = newsResult.data;
      const now = new Date();

      let data: Array<{
        period: string;
        published: number;
        views: number;
        engagementRate: number;
        topPerforming?: { title: string; views: number };
      }> = [];

      switch (period) {
        case 'weekly':
          for (let i = 7; i >= 0; i--) {
            const weekStart = new Date(now);
            weekStart.setDate(weekStart.getDate() - (i * 7));
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekEnd.getDate() + 7);

            const weekNews = allNews.filter(n => {
              const newsDate = n.date ? new Date(n.date) : null;
              return newsDate && newsDate >= weekStart && newsDate < weekEnd;
            });

            const weekViews = weekNews.reduce((sum, n) => sum + (n.views || 0), 0);
            const engagementRate = weekNews.length > 0 ? Math.round((weekViews / weekNews.length) / 100) : 0;
            const topArticle = weekNews.sort((a, b) => (b.views || 0) - (a.views || 0))[0];

            data.push({
              period: `Week ${i + 1}`,
              published: weekNews.length,
              views: weekViews,
              engagementRate,
              topPerforming: topArticle ? {
                title: topArticle.title.length > 20 ? topArticle.title.substring(0, 20) + '...' : topArticle.title,
                views: topArticle.views || 0
              } : undefined
            });
          }
          break;

        case 'monthly':
          for (let i = 5; i >= 0; i--) {
            const month = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1);

            const monthNews = allNews.filter(n => {
              const newsDate = n.date ? new Date(n.date) : null;
              return newsDate && newsDate >= month && newsDate < nextMonth;
            });

            const monthViews = monthNews.reduce((sum, n) => sum + (n.views || 0), 0);
            const engagementRate = monthNews.length > 0 ? Math.round((monthViews / monthNews.length) / 100) : 0;
            const topArticle = monthNews.sort((a, b) => (b.views || 0) - (a.views || 0))[0];

            data.push({
              period: month.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
              published: monthNews.length,
              views: monthViews,
              engagementRate,
              topPerforming: topArticle ? {
                title: topArticle.title.length > 20 ? topArticle.title.substring(0, 20) + '...' : topArticle.title,
                views: topArticle.views || 0
              } : undefined
            });
          }
          break;

        case 'yearly':
          for (let i = 2; i >= 0; i--) {
            const year = new Date(now.getFullYear() - i, 0, 1);
            const nextYear = new Date(year.getFullYear() + 1, 0, 1);

            const yearNews = allNews.filter(n => {
              const newsDate = n.date ? new Date(n.date) : null;
              return newsDate && newsDate >= year && newsDate < nextYear;
            });

            const yearViews = yearNews.reduce((sum, n) => sum + (n.views || 0), 0);
            const engagementRate = yearNews.length > 0 ? Math.round((yearViews / yearNews.length) / 100) : 0;

            data.push({
              period: year.getFullYear().toString(),
              published: yearNews.length,
              views: yearViews,
              engagementRate
            });
          }
          break;
      }

      return { success: true, data };
    } catch (error: any) {
      console.error('Error getting news trends:', error);
      return { success: false, error: error.message };
    }
  }, ttlMap[period] || 30 * 60 * 1000);
};

// Track a single site visit/page view
export const recordSiteVisit = async (path: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const normalizedPath = path && path.trim() ? path.trim() : '/';
    await addDoc(collection(db, COLLECTIONS.SITE_VISITS), {
      path: normalizedPath,
      visitedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error: any) {
    console.error('Error recording site visit:', error);
    return { success: false, error: error?.message || 'Failed to record site visit' };
  }
};

// Aggregate site visits into trend buckets for dashboard charts
export const getSiteVisitTrends = async (
  period: 'weekly' | 'monthly' | 'yearly' = 'weekly'
): Promise<{
  success: boolean;
  data?: Array<{ period: string; visits: number }>;
  error?: string;
}> => {
  try {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    let queryStart = new Date(today);
    switch (period) {
      case 'weekly':
        queryStart.setDate(queryStart.getDate() - 55); // 8 contiguous 7-day buckets
        break;
      case 'monthly':
        queryStart = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        break;
      case 'yearly':
        queryStart = new Date(now.getFullYear() - 2, 0, 1);
        break;
      default:
        queryStart.setDate(queryStart.getDate() - 55);
    }

    const visitsQuery = query(
      collection(db, COLLECTIONS.SITE_VISITS),
      where('visitedAt', '>=', Timestamp.fromDate(queryStart)),
      orderBy('visitedAt', 'asc')
    );

    const visitSnapshot = await getDocs(visitsQuery);

    const toDayKey = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    const dayCounts = new Map<string, number>();
    visitSnapshot.forEach((snap) => {
      const data = snap.data() as { visitedAt?: Timestamp };
      const visitDate = data.visitedAt?.toDate?.();
      if (!visitDate) return;
      const dayKey = toDayKey(visitDate);
      dayCounts.set(dayKey, (dayCounts.get(dayKey) || 0) + 1);
    });

    const sumVisitsInRange = (start: Date, endExclusive: Date) => {
      let total = 0;
      const cursor = new Date(start);
      while (cursor < endExclusive) {
        total += dayCounts.get(toDayKey(cursor)) || 0;
        cursor.setDate(cursor.getDate() + 1);
      }
      return total;
    };

    const data: Array<{ period: string; visits: number }> = [];

    if (period === 'weekly') {
      for (let i = 7; i >= 0; i--) {
        const bucketEnd = new Date(today);
        bucketEnd.setDate(bucketEnd.getDate() - (i * 7) + 1); // exclusive

        const bucketStart = new Date(bucketEnd);
        bucketStart.setDate(bucketStart.getDate() - 7);

        data.push({
          period: `Week ${8 - i}`,
          visits: sumVisitsInRange(bucketStart, bucketEnd)
        });
      }
    } else if (period === 'monthly') {
      for (let i = 5; i >= 0; i--) {
        const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1);
        data.push({
          period: monthStart.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
          visits: sumVisitsInRange(monthStart, nextMonth)
        });
      }
    } else {
      for (let i = 2; i >= 0; i--) {
        const yearStart = new Date(now.getFullYear() - i, 0, 1);
        const nextYear = new Date(yearStart.getFullYear() + 1, 0, 1);
        data.push({
          period: yearStart.getFullYear().toString(),
          visits: sumVisitsInRange(yearStart, nextYear)
        });
      }
    }

    return { success: true, data };
  } catch (error: any) {
    console.error('Error getting site visit trends:', error);
    return { success: false, error: error?.message || 'Failed to load site visit trends' };
  }
};


export const deleteNews = async (id: string) => {
  try {
    await deleteDoc(doc(db, COLLECTIONS.NEWS, id));
    // Invalidate related caches after deletion
    try {
      invalidateNewsCaches();
    } catch (e) {
      // non-fatal
    }
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Update the getNews function to include visibility fields
export const getNews = async (options: {
  limitCount?: number;
  onlyPublished?: boolean;
  category?: string;
  lastDoc?: DocumentSnapshot;
  page?: number;
} = {}) => {
  try {
    const {
      limitCount = 10,
      onlyPublished = true,
      category,
      lastDoc,
      page = 1
    } = options;

    // Generate cache key
    const cacheKey = `${PUBLIC_NEWS_CACHE_VERSION}_news_page_${page}_${limitCount}_${onlyPublished}_${category || 'all'}`;

    // Try cache first
    const cached = cache.getCacheState(cacheKey);
    if (cached.hasCache && cached.isFresh) {
      const cachedData = await cache.get(cacheKey, async () => ({ success: true, data: [] }));
      return { 
        ...cachedData, 
        fromCache: true 
      };
    }

    console.log('🔍 Fetching news...');


    let constraints: any[] = [];
    
    // Server-side filtering
    if (onlyPublished) {
      constraints.push(where('isPublished', '==', true));
    }
    
    if (category) {
      constraints.push(where('category', '==', category));
    }
    
    // Always order by publishedAt or createdAt
    constraints.push(orderBy('publishedAt', 'desc'));
    
    // Apply pagination
    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }
    
    constraints.push(limit(limitCount));
    
    const q = query(collection(db, COLLECTIONS.NEWS), ...constraints);
    
    let querySnapshot = await getDocs(q);

    if (querySnapshot.empty && onlyPublished && !lastDoc) {
      const fallbackConstraints: any[] = [where('isPublished', '==', true)];
      if (category) {
        fallbackConstraints.push(where('category', '==', category));
      }
      fallbackConstraints.push(orderBy('createdAt', 'desc'), limit(limitCount));
      querySnapshot = await getDocs(query(collection(db, COLLECTIONS.NEWS), ...fallbackConstraints));
    }
    
    const news = querySnapshot.docs.map((doc) => mapNewsDoc(doc));

    const result = {
      success: true,
      data: news,
      lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1],
      fromCache: false
    };
    
    // Cache the result
    await cache.get(cacheKey, async () => result, PUBLIC_LIST_CACHE_TTL);
    
    return result;
  } catch (error: any) {
    console.error('❌ Error fetching news:', error);
    return { 
      success: false, 
      error: error.message, 
      data: [], 
      fromCache: false 
    };
  }
};

export const getNewsById = async (id: string) => {
  try {
    const docRef = doc(db, COLLECTIONS.NEWS, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const mappedNews = mapNewsDoc(docSnap);
      return {
        success: true,
        data: {
          ...mappedNews,
        }
      };
    }
    return { success: false, error: 'News not found' };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};




//--- Team Members operations
export const getTeamMembers = async (onlyPublished: boolean = true) => {
  try {
    const constraints: any[] = [];
    if (onlyPublished) constraints.push(where('isPublished', '==', true));
    constraints.push(orderBy('createdAt', 'desc'));

    const q = query(collection(db, 'teamMembers'), ...constraints);
    const querySnapshot = await getDocs(q);

    const members = querySnapshot.docs.map(doc => {
      const data: any = doc.data();
      return {
        id: doc.id,
        name: data.name || data.title || '',
        position: data.position || '',
        positionOther: data.positionOther || '',
        bio: data.bio || data.content || '',
        imageUrl: data.imageUrl || '',
        email: data.email || '',
        department: data.department || '',
        isPublished: !!data.isPublished,
        createdAt: data.createdAt?.toDate?.().toISOString?.() || data.createdAt || null,
        updatedAt: data.updatedAt?.toDate?.().toISOString?.() || data.updatedAt || null
      };
    });

    return { success: true, data: members };
  } catch (error: any) {
    return { success: false, error: error.message, data: [] };
  }
};

// Team members CRUD
export const createTeamMember = async (memberData: any, authorId: string) => {
  try {
    const docRef = await addDoc(collection(db, 'teamMembers'), {
      ...memberData,
      author: authorId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return { success: true, id: docRef.id };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const updateTeamMember = async (id: string, memberData: any) => {
  try {
    const memberDoc = doc(db, 'teamMembers', id);
    await updateDoc(memberDoc, {
      ...memberData,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const deleteTeamMember = async (id: string) => {
  try {
    await deleteDoc(doc(db, 'teamMembers', id));
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getArticles = async (options: { limitCount?: number } = {}) => {
  const { limitCount = 6 } = options;
  const cacheKey = `home_cards_news_${limitCount}`;
  return await cache.get(cacheKey, async () => {
    try {
      console.log('🔄 Fetching cards news from Firebase...');

      const q = query(
        collection(db, COLLECTIONS.NEWS),
        where('isPublished', '==', true),
        where('showInCards', '==', true),
        orderBy('publishedAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      console.log(`✅ Found ${querySnapshot.size} card news items`);

      const news = querySnapshot.docs.map((doc) =>
        mapNewsDoc(doc, {
          excerptLength: 120,
          defaultCategory: 'Update',
          defaultImage: 'https://images.unsplash.com/photo-1559028012-481c04fa702d?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80'
        })
      );
      console.log('📦 Cards news data:', news);
      return { success: true, data: news };
    } catch (error: any) {
      console.error('❌ Error in getNewsForCards:', error);
      return { success: false, error: error.message, data: [] };
    }
  }, PUBLIC_LIST_CACHE_TTL);
};

// Upload an image to Firebase Storage and return its download URL
export const uploadNewsImage = async (file: any, folder: string = 'news-images'): Promise<string> => {
  try {
    const fileName = `${folder}/${Date.now()}_${(file && file.name) || 'image'}`;
    const storageRef = ref(storage, fileName);
    const snapshot = await uploadBytes(storageRef, file);
    const url = await getDownloadURL(snapshot.ref);
    return url;
  } catch (error: any) {
    console.error('Error uploading news image:', error);
    throw new Error(error?.message || 'Failed to upload image');
  }
};

// Get distinct news categories (cached)
export const getNewsCategories = async () => {
  const cacheKey = 'news_categories';
  return await cache.get(cacheKey, async () => {
    try {
      const q = query(collection(db, COLLECTIONS.NEWS), where('isPublished', '==', true), limit(100));
      const snap = await getDocs(q);
      const categories = new Set<string>();
      snap.forEach((d) => {
        const c = (d.data() as any).category;
        if (c) categories.add(c);
      });
      return { success: true, data: Array.from(categories) };
    } catch (error: any) {
      return { success: false, error: error.message, data: [] };
    }
  }, PUBLIC_STATIC_CACHE_TTL);
};

export const deleteNewsImage = async (imageUrl: string) => {
  try {
    // Extract path from URL
    const imageRef = ref(storage, imageUrl);
    await deleteObject(imageRef);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getPrograms = async (filters: any = {}) => {
  try {
    const constraints: any[] = [];
    
    if (filters.status) {
      constraints.push(where('status', '==', filters.status));
    }
    
    if (filters.category) {
      constraints.push(where('category', '==', filters.category));
    }
    
    constraints.push(orderBy('createdAt', 'desc'));
    
    const q = query(collection(db, COLLECTIONS.PROGRAMS), ...constraints);
    
    const querySnapshot = await getDocs(q);
    const programs: any[] = [];
    
    querySnapshot.forEach((doc) => {
      programs.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: programs };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const getUserProfile = async (userId: string) => {
  try {
    const docRef = doc(db, COLLECTIONS.USERS, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: false, error: 'User not found' };
    }
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const updateUserProfileData = async (userId: string, data: any) => {
  try {
    const docRef = doc(db, COLLECTIONS.USERS, userId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp()
    });
    // Invalidate user-related caches
    try {
      cache.invalidate('users.all');
      cache.invalidate('users.all.includeAdmins');
      cache.invalidate('users.stats');
      cache.invalidatePattern(/^users\.growth\./);
    } catch (e) {}
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

export const createUserProfile = async (uid: string, profileData: any) => {
  try {
    const userRef = doc(db, COLLECTIONS.USERS, uid);
    await setDoc(userRef, {
      ...profileData,
      createdAt: profileData.createdAt || serverTimestamp(),
      updatedAt: profileData.updatedAt || serverTimestamp(),
    });
    // Invalidate user list and stats caches after creating a user
    try {
      cache.invalidate('users.all');
      cache.invalidate('users.all.includeAdmins');
      cache.invalidate('users.stats');
      cache.invalidatePattern(/^users\.growth\./);
    } catch (e) {}
    return { success: true };
  } catch (error: any) {
    console.error('Error creating user profile:', error);
    return { success: false, error: error.message };
  }
};


// Batch update news views to reduce writes
const viewIncrementQueue = new Map<string, number>();
let viewIncrementTimer: NodeJS.Timeout | null = null;

export const incrementNewsViews = async (id: string) => {
  // Queue the increment
  const currentCount = viewIncrementQueue.get(id) || 0;
  viewIncrementQueue.set(id, currentCount + 1);
  
  // Debounce the update
  if (viewIncrementTimer) {
    clearTimeout(viewIncrementTimer);
  }
  
  viewIncrementTimer = setTimeout(async () => {
    const batch = writeBatch(db);
    
    for (const [newsId, incrementCount] of viewIncrementQueue.entries()) {
      if (incrementCount > 0) {
        const docRef = doc(db, COLLECTIONS.NEWS, newsId);
        batch.update(docRef, {
          views: increment(incrementCount),
          updatedAt: serverTimestamp()
        });
      }
    }
    
    try {
      await batch.commit();
      console.log(`✅ Updated views for ${viewIncrementQueue.size} news items`);
      viewIncrementQueue.clear();
    } catch (error) {
      console.error('Error updating news views:', error);
    }
  }, 5000); // Update every 5 seconds
};
